import React, {
  useId,
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
  useTransition,
} from "react";
import { Handle, Position } from "reactflow";
import { createPortal } from "react-dom";
import {
  Button,
  Menu,
  NativeSelect,
  Tooltip,
  useMantineColorScheme,
} from "@mantine/core";
import useStore from "./store";
import Plot from "react-plotly.js";
// The Plotly bundle react-plotly.js renders with (importing "plotly.js" would
// add a second copy), for resizing plots ourselves. It has no type declarations.
// @ts-expect-error No declaration file for plotly.js/dist/plotly
import Plotly from "plotly.js/dist/plotly";
import BaseNode from "./BaseNode";
import NodeLabel from "./NodeLabelComponent";
import ResizeHandle from "./ResizeHandle";
import {
  cleanMetavarsFilterFunc,
  llmResponseDataToString,
  truncStr,
} from "./backend/utils";
import {
  Dict,
  EvaluationResults,
  EvaluationScore,
  JSONCompatible,
  LLMResponse,
  LLMResponseData,
  LLMSpec,
} from "./backend/typing";
import { Status } from "./StatusIndicatorComponent";
import { grabResponses } from "./backend/backend";
import { StringLookup } from "./backend/cache";
import {
  IconAlertTriangle,
  IconChartBar,
  IconChartHistogram,
  IconBlur,
  IconChartArea,
} from "@tabler/icons-react";
import {
  AIGenPlotPopover,
  AIPlotHeaderButtons,
  AIPlotView,
} from "./VisNodeAIPlot";
import { AIPlot } from "./backend/aiPlots";
import {
  describeConditions,
  ecologitsCovers,
  plottableStat,
  plottableStatsIn,
  statsAsScores,
} from "./backend/responseStats";

/**
 * STATS
 */
import {
  max,
  mean,
  median,
  min,
  quantile,
  sampleStandardDeviation,
  sum,
} from "simple-statistics";
// import * as jStat from "jstat"; // jStat is a pure JS library without types

// FUTURE: Including in-progress error bar computation for future use.
// const bootstrapCI = (
//   values: number[],
//   numSamples = 1000,
//   alpha = 0.05,
//   overFunc?: (ns: number[]) => number,
// ) => {
//   const means = [];
//   const f = overFunc ?? mean;
//   for (let i = 0; i < numSamples; i++) {
//     // Resample with replacement
//     const resampled = sampleWithReplacement(values, values.length, Math.random);
//     means.push(f(resampled));
//   }

//   // Compute percentiles for the confidence interval
//   const lowerBound = quantile(means, alpha / 2); // 2.5th percentile
//   const upperBound = quantile(means, 1 - alpha / 2); // 97.5th percentile

//   return {
//     ciMean: mean(means), // Bootstrap mean (could be slightly different from original mean)
//     lowerBound: lowerBound,
//     upperBound: upperBound,
//   };
// };

// /**
//  * Computes the lower and upper bound for an error bar to display in a Plotly plot.
//  * @param samples The samples to compute the error bar for
//  * @param scaleBy A value to scale the outputs by
//  * @param overFunc The default function is mean. However, we might want to calculate CI over other values, such as sum or SD. In this case, we must use bootstrapping, since the t-stat standard error method doesn't work in these cases.
//  * @returns The [lowerBound, upperBound] values as a 2-item array, normalized to 100%.
//  */
// const computeErrorBar = (
//   samples: number[],
//   scaleBy?: number,
//   overFunc?: (ns: number[]) => number,
// ) => {
//   // FUTURE: Implement a more reliable method for computing error bars
//   if (samples.length < 2) return [0, 0]; // Not enough information
//   const scalar = scaleBy ?? 1.0;

//   // Choose method depending on # of samples
//   // NOTE: The cutoff here is informed by research of Zhu & Kolassa (https://doi.org/10.1080/03610918.2017.1348516)
//   //       which shows that below sample size 50, t-test provides a more reliable predictor of actual CI than bootstrapping methods.
//   if (samples.length < 50 && !overFunc) {
//     // Fallback to standard error-based confidence interval using t-stat
//     const se = standardDeviation(samples) / Math.sqrt(samples.length); // Compute standard Error
//     const t_value = (jStat as any).studentt.inv(
//       1 - (1 - 0.95) / 2,
//       samples.length - 1,
//     ); // 95% CI (assuming normality)
//     const m = mean(samples);
//     console.warn("Error bar t-stat:", m, se, t_value);
//     return [(m - t_value * se) * scalar, (m + t_value * se) * scalar];
//   } else {
//     // Compute a bootstrap 95% CI (confidence interval).
//     //  NOTE: We use bootstrapping because with prompts, we can *never* assume
//     //  the sample of LLM outputs is representative of the population for the user's hypothesis.
//     //  LLM outputs also don't have to follow a normal distribution.
//     //  This is resource-intensive but a much more reliable approx. than standard error/dev.
//     const { ciMean, lowerBound, upperBound } = bootstrapCI(
//       samples,
//       1000,
//       0.05,
//       overFunc,
//     );
//     console.warn("Error bar 95% CI:", ciMean, lowerBound, upperBound);
//     return [(ciMean - lowerBound) * scalar, (upperBound - ciMean) * scalar];
//   }
// };

const castEvalScoreToNum = (score: EvaluationScore): number => {
  if (typeof score === "number") return score;
  else if (typeof score === "boolean") return score === true ? 1 : 0;
  else return 0; // unknown, soft fail
};

/**
 * An invisible point on a box's median that shows one summary of the box on
 * hover. Plotly's own hover over a box labels each of its stats separately
 * (min, fences, quartiles, median, max), piled on top of each other, and
 * can't be templated, so boxes hover only on their points (`hoveron`) and
 * this gives the summary instead. The same goes for violins.
 */
const boxSummaryTrace = (
  boxes: { y: string | number; title: string; values: number[] }[],
  color: string,
  // For grouped boxes: the boxes' offsetgroup, so the points sit on them
  // (with the layout's scattermode "group")
  offsetgroup?: string,
): Dict | undefined => {
  const nonEmpty = boxes.filter((b) => b.values.length > 0);
  if (nonEmpty.length === 0) return undefined;
  const fmt = (v: number) => String(Number(v.toPrecision(3)));
  const summary = ({ title, values }: { title: string; values: number[] }) => {
    const range =
      values.length > 1
        ? ` · range ${fmt(min(values))}–${fmt(max(values))}`
        : "";
    return `<b>${title}</b><br>median ${fmt(median(values))}${range} · n = ${values.length}`;
  };
  return {
    type: "scatter",
    mode: "markers",
    x: nonEmpty.map((b) => median(b.values)),
    y: nonEmpty.map((b) => b.y),
    text: nonEmpty.map(summary),
    orientation: "h",
    marker: { color, size: 16, opacity: 0 },
    hoverlabel: { bgcolor: color, align: "left" },
    hovertemplate: "%{text}<extra></extra>",
    showlegend: false,
    ...(offsetgroup !== undefined ? { offsetgroup } : {}),
  };
};

/**
 * Makes a box plot trace a violin instead, keeping its points. Its outline
 * stops at the lowest and highest values (Plotly's default runs past them,
 * suggesting values no one got), with a small box inside for the quartiles.
 */
const asViolin = (d: Dict) => {
  d.type = "violin";
  d.points = "all";
  delete d.boxpoints;
  d.spanmode = "hard";
  d.box = { visible: true };
};

/** One strip of a density gradient plot, at a height on a numeric y axis. */
interface DensityBand {
  y: number;
  halfHeight: number;
  values: number[];
  // Hover text for each value
  texts: string[];
  title: string;
  color: string;
}

/** A colour's red, green and blue, for shading it; gray if it isn't hex. */
const hexToRgb = (color: string): [number, number, number] => {
  let hex = color.trim().replace(/^#/, "");
  if (hex.length === 3)
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  if (!/^[0-9a-f]{6}$/i.test(hex)) return [136, 136, 136];
  return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [
    number,
    number,
    number,
  ];
};

/** A colour mixed with black (negative `amount`) or white (positive). */
const shade = (color: string, amount: number): string => {
  const [r, g, b] = hexToRgb(color).map((c) =>
    Math.round(amount < 0 ? c * (1 + amount) : c + (255 - c) * amount),
  );
  return `rgb(${r},${g},${b})`;
};

/**
 * The density of values at each point, by a Gaussian kernel density estimate
 * with Silverman's rule for the bandwidth. `fallbackBandwidth` is for values
 * with no spread (e.g. all the same), where the rule gives zero.
 * `minBandwidth` should be the spacing of the points: a narrower kernel can
 * fall between them, leaving the density 0 (or nearly) at every one.
 */
const kernelDensity = (
  values: number[],
  points: number[],
  fallbackBandwidth: number,
  minBandwidth: number,
): number[] => {
  const n = values.length;
  let bw = 0;
  if (n > 1) {
    const sd = sampleStandardDeviation(values);
    const iqr = quantile(values, 0.75) - quantile(values, 0.25);
    const spread = iqr > 0 ? Math.min(sd, iqr / 1.34) : sd;
    bw = 0.9 * spread * Math.pow(n, -1 / 5);
  }
  if (!(bw > 0)) bw = fallbackBandwidth;
  bw = Math.max(bw, minBandwidth);
  return points.map(
    (p) =>
      values.reduce((acc, v) => acc + Math.exp(-0.5 * ((p - v) / bw) ** 2), 0) /
      (n * bw * Math.sqrt(2 * Math.PI)),
  );
};

/**
 * Traces for a density gradient plot: each band is a strip shaded by how
 * densely its values fall there (darkest at its densest), with a tick for
 * every value and a thicker one for the median, in a darker shade of its colour.
 * On a dark background, ticks where the shading is faint (e.g. outliers) would
 * disappear in a darker shade, so there they go lighter instead. Descriptive only: the shading
 * is the data's own distribution, not a confidence interval.
 *
 * The y axis must be numeric (bands sit at their `y`); see setDensityStripAxes.
 */
const densityStripTraces = (bands: DensityBand[], darkMode: boolean) => {
  const nonEmpty = bands.filter((b) => b.values.length > 0);
  if (nonEmpty.length === 0) return [];

  // One x grid for all the bands, over the range of all their values, so
  // strips line up and none is shaded beyond any value actually seen
  const all = nonEmpty.flatMap((b) => b.values);
  let lo = min(all);
  let hi = max(all);
  if (hi === lo) {
    lo -= 0.5;
    hi += 0.5;
  }
  const cells = 120;
  const step = (hi - lo) / cells;
  const edges = Array.from({ length: cells + 1 }, (_, i) => lo + i * step);
  const centers = edges.slice(0, -1).map((e) => e + step / 2);

  const fallbackBandwidth = (hi - lo) * 0.02;
  const darker = (color: string) => shade(color, -0.45);
  // A tick's colour, by how dense (0 to 1) the shading is where it sits
  const tickColor = (color: string, density: number) => {
    if (!darkMode) return darker(color);
    // From lighter where faint, through the colour itself, to darker where dense
    const t = Math.min(1, Math.max(0, (density - 0.1) / 0.5));
    return shade(color, 0.45 - 0.9 * t);
  };

  const traces: Dict[] = [];
  nonEmpty.forEach((b) => {
    const density = kernelDensity(b.values, centers, fallbackBandwidth, step);
    const peak = max(density);
    const shading = density.map((d) => (peak > 0 ? d / peak : 0));
    // The shading under each value, read off the grid (between its two
    // nearest cells) rather than worked out again for every value
    const shadingAt = (v: number) => {
      const pos = Math.min(cells - 1, Math.max(0, (v - lo) / step - 0.5));
      const i = Math.floor(pos);
      const j = Math.min(cells - 1, i + 1);
      return shading[i] + (shading[j] - shading[i]) * (pos - i);
    };
    const [r, g, bl] = hexToRgb(b.color);
    traces.push({
      type: "heatmap",
      // One row of cells, given by its edges
      x: edges,
      y: [b.y - b.halfHeight, b.y + b.halfHeight],
      z: [shading],
      zmin: 0,
      zmax: 1,
      colorscale: [
        [0, `rgba(${r},${g},${bl},0)`],
        [1, `rgba(${r},${g},${bl},0.85)`],
      ],
      showscale: false,
      hoverinfo: "skip",
    });
    // A tick per value, so the number of values and each one stay visible
    traces.push({
      type: "scatter",
      mode: "markers",
      x: b.values,
      y: b.values.map(() => b.y),
      text: b.texts,
      hovertemplate: "%{text} <b><i>(%{x})</i></b><extra></extra>",
      marker: {
        // An open symbol's stroke takes the marker's colour, not the line's
        symbol: "line-ns-open",
        color: b.values.map((v) => tickColor(b.color, shadingAt(v))),
        size: Math.max(6, 36 * b.halfHeight),
        opacity: 0.8,
        line: { width: 1 },
      },
      showlegend: false,
    });
  });

  traces.push({
    type: "scatter",
    mode: "markers",
    x: nonEmpty.map((b) => median(b.values)),
    y: nonEmpty.map((b) => b.y),
    marker: {
      symbol: "line-ns-open",
      color: nonEmpty.map((b) => darker(b.color)),
      size: nonEmpty.map((b) => Math.max(8, 60 * b.halfHeight)),
      line: { width: 3 },
    },
    hoverinfo: "skip",
    showlegend: false,
  });
  nonEmpty.forEach((b) => {
    const summary = boxSummaryTrace(
      [{ y: b.y, title: b.title, values: b.values }],
      b.color,
    );
    if (summary) traces.push(summary);
  });
  return traces;
};

/**
 * Sets up a layout's axes for densityStripTraces: a numeric y axis labelled
 * like a category one, one row per label. Heatmaps make Plotly add tick marks
 * to both axes, which the other plots don't have, so those are turned off.
 */
const setDensityStripAxes = (layout: Dict, labels: string[]) => {
  layout.yaxis = {
    ...layout.yaxis,
    type: "linear",
    tickvals: labels.map((_, i) => i),
    ticktext: labels,
    range: [-0.5, labels.length - 0.5],
    zeroline: false,
    showgrid: false,
    ticks: "",
  };
  layout.xaxis = { ...layout.xaxis, ticks: "" };
};

/**
 * The model specs the flow's nodes choose (e.g. a Prompt Node's), by their
 * nicknames, which is how responses name the model that gave them. Looks in
 * nested lists too, e.g. a group of models' items. A nickname given to two
 * different models maps to undefined.
 */
const modelSpecsByNickname = (): Map<string, LLMSpec | undefined> => {
  const specs = new Map<string, LLMSpec | undefined>();
  const visit = (value: unknown, depth: number) => {
    if (depth > 4 || value === null || typeof value !== "object") return;
    if (Array.isArray(value)) {
      value.forEach((item) => visit(item, depth + 1));
      return;
    }
    const item = value as Dict;
    if (typeof item.name === "string" && typeof item.model === "string") {
      const prev = specs.get(item.name);
      specs.set(
        item.name,
        specs.has(item.name) && prev?.model !== item.model
          ? undefined
          : (item as LLMSpec),
      );
    } else if (Array.isArray(item.items)) visit(item.items, depth + 1); // a group
  };
  (useStore.getState().nodes ?? []).forEach((node) =>
    Object.values(node.data ?? {}).forEach((value) => {
      if (Array.isArray(value)) visit(value, 0);
    }),
  );
  return specs;
};

/** Hides text on screen while leaving it to screen readers. */
const visuallyHidden: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
};

/** The name of the model that gave a response. */
const llmNameOf = (r: LLMResponse): string =>
  typeof r.llm === "string" || typeof r.llm === "number"
    ? String(StringLookup.get(r.llm) ?? r.llm)
    : r.llm?.name ?? "(unknown)";

const findEvalResKeys = (resps: LLMResponse[]): Set<string> => {
  const eval_res_keys = new Set<string>();
  resps.forEach((resp_obj) => {
    if (resp_obj.eval_res && resp_obj.eval_res.items) {
      resp_obj.eval_res.items.forEach((item) => {
        if (typeof item === "object") {
          Object.keys(item).forEach((k) => eval_res_keys.add(k));
        } else {
          // If the item is not an object, we can assume it's a single value
          eval_res_keys.add("score");
        }
      });
    }
  });
  return eval_res_keys;
};

/**
 *  UTIL FUNCTIONS FOR VIS PLOTS
 */

const smallTextStyle: React.CSSProperties = {
  fontSize: "13px",
  margin: "6pt 3pt 0 3pt",
  fontWeight: "bold",
  whiteSpace: "nowrap",
};

const splitAndAddBreaks = (s: string, chunkSize: number) => {
  // Split the input string into chunks of specified size
  const chunks: string[] = [];
  for (let i = 0; i < s.length; i += chunkSize) {
    chunks.push(s.slice(i, i + chunkSize));
  }
  // Join the chunks with a <br> tag
  return chunks.join("<br>");
};

// Create HTML for hovering over a single datapoint. We must use 'br' to specify line breaks.
const createHoverTexts = (responses: string[]) => {
  const max_len = 500;
  return responses
    .map((s) => {
      // If responses were reduced across dimensions, this could include several. Pick the first and mark it as one of many:
      if (Array.isArray(s)) {
        const s_len = s.length;
        return s.map(
          (substr, idx) =>
            splitAndAddBreaks(truncStr(substr, max_len) ?? "", 60) +
            `<br><b>(${idx + 1} of ${s_len})</b>`,
        );
      } else return [splitAndAddBreaks(truncStr(s, max_len) ?? "", 60)];
    })
    .flat();
};

const getUniqueKeysInResponses = (
  responses: LLMResponse[],
  keyFunc: (r: LLMResponse) => string,
) => {
  const ukeys = new Set<string>();
  responses.forEach((res_obj) => ukeys.add(keyFunc(res_obj)));
  return Array.from(ukeys);
};

const areSetsEqual = (xs: Set<any>, ys: Set<any>) =>
  xs.size === ys.size && [...xs].every((x) => ys.has(x));

/**
 * Wraps a label to lines of at most `max_line_len` characters, breaking
 * between words. A word longer than a line is split with a hyphen, as before.
 */
function addLineBreaks(str: string, max_line_len: number) {
  if (!str || typeof str !== "string" || str.length === 0) return "";

  // Split an over-long word across lines, hyphenating where it breaks mid-word
  const splitLongWord = (word: string) => {
    const pieces: string[] = [];
    let rest = word;
    while (rest.length > max_line_len) {
      const head = rest.slice(0, max_line_len - 1);
      pieces.push(/[A-Za-z]$/.test(head) ? `${head}-` : head);
      rest = rest.slice(max_line_len - 1);
    }
    pieces.push(rest);
    return pieces;
  };

  const lines: string[] = [];
  let line = "";
  for (const word of str.split(" ")) {
    if (word.length > max_line_len) {
      if (line) lines.push(line);
      const pieces = splitLongWord(word);
      lines.push(...pieces.slice(0, -1));
      line = pieces[pieces.length - 1];
    } else if (line.length === 0) line = word;
    else if (line.length + 1 + word.length <= max_line_len) line += " " + word;
    else {
      lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  return lines.join("<br>");
}

const genUniqueShortnames = (
  names: Iterable<string>,
  max_chars_per_line = 32,
) => {
  // Generate unique 'shortnames' to refer to each name:
  const past_shortnames_counts: Dict<number> = {};
  const shortnames: Dict<string> = {};
  const max_lines = 8;
  for (const name of names) {
    // Truncate string up to maximum num of chars
    let sn = truncStr(name, max_chars_per_line * max_lines - 3) ?? "";
    // Add <br> tags to spread across multiple lines, where necessary
    sn = addLineBreaks(sn, max_chars_per_line);
    if (sn in past_shortnames_counts) {
      past_shortnames_counts[sn] += 1;
      shortnames[name] = sn + `(${past_shortnames_counts[sn]})`;
    } else {
      shortnames[name] = sn;
      past_shortnames_counts[sn] = 1;
    }
  }
  return shortnames;
};

const calcMaxCharsPerLine = (shortnames: string[]) => {
  let max_chars = 1;
  for (let i = 0; i < shortnames.length; i++) {
    const sn = shortnames[i];
    if (sn.includes("<br>")) return sn.indexOf("<br>");
    else if (sn.length > max_chars) max_chars = sn.length;
  }
  return Math.max(max_chars, 9);
};

const calcLeftPaddingForYLabels = (shortnames: string[]) => {
  return calcMaxCharsPerLine(shortnames) * 7.3;
};

interface VisNodeData {
  vars: { value: string; label: string }[];
  selected_vars: string[] | string;
  llm_groups?: { value: string; label: string }[];
  selected_llm_group?: string;
  eval_res_vars?: string[];
  selected_eval_res_var?: string;
  input: string;
  refresh: boolean;
  title: string;
  // A plot the AI made, shown instead of the default plot until the user goes back
  aiPlot?: AIPlot | null;
  // Bar chart, box plot, violin or density gradient, as last chosen (see GRAPH_OPTIONS)
  graph_type?: string;
  // The plot's size, as last resized, in pixels
  plot_size?: { width: number; height: number };
}

/**
 * Graph types to choose between, for data that can be shown either way.
 * Defined once, so a graph type keeps its identity across renders.
 */
const GRAPH_OPTIONS = [
  { key: "bar", label: "Bar Chart", icon: <IconChartBar size={18} /> },
  {
    key: "box",
    label: "Box & Whiskers",
    icon: <IconChartHistogram size={18} />,
  },
  { key: "violin", label: "Violin", icon: <IconChartArea size={18} /> },
  { key: "gradient", label: "Density Gradient", icon: <IconBlur size={18} /> },
];

/**
 * The fewest values a violin or density strip needs. With fewer, its shape
 * mostly shows the smoothing rather than the data, so a box plot (with every
 * point) is drawn instead.
 */
const MIN_VALUES_FOR_DENSITY = 10;
const isDensityGraph = (key: string) => key === "violin" || key === "gradient";

/**
 * VIS VIEW COMPONENT
 * The inner part of the Vis Node.
 */
export interface VisViewProps {
  responses: LLMResponse[];
  wideFormat?: boolean;
  id?: string;
  data?: VisNodeData;
  whenReplotting?: (isReplotting: boolean) => void;
  /** Where to put the chart type button, e.g. the node's header; else in the toolbar. */
  headerSlot?: HTMLElement | null;
}
export interface VisViewRef {
  resetControls: (responses: LLMResponse[]) => void;
}

/**
 * Inner component for code evaluators/processors, storing the body of the UI (outside of the header and footers).
 */
export const VisView = forwardRef<VisViewRef, VisViewProps>(
  function VisViewComponent(
    {
      responses: inputResponses,
      id,
      data,
      whenReplotting,
      wideFormat,
      headerSlot,
    },
    ref,
  ) {
    // Color scheme
    const { colorScheme } = useMantineColorScheme();

    const setDataPropsForNode = useStore((state) => state.setDataPropsForNode);
    const getColorForLLMAndSetIfNotFound = useStore(
      (state) => state.getColorForLLMAndSetIfNotFound,
    );

    const plotDivRef = useRef<HTMLDivElement | null>(null);
    const plotlyRef = useRef<Plot>(null);
    const [plotlySpec, setPlotlySpec] = useState<Dict[]>([]);
    const [plotlyLayout, setPlotlyLayout] = useState({});

    // So updating the plot doesn't block the UI
    const [isPlotRerenderPending, startTransition] = useTransition();

    // For some data types, there are multiple graph options available...
    // Saved with the node, so it's kept when the flow is saved and loaded
    const [graphType, setGraphType] = useState(
      GRAPH_OPTIONS.find((o) => o.key === data?.graph_type) ?? GRAPH_OPTIONS[0],
    );
    // ...and followed if the node's data changes (e.g. a flow loaded in its place)
    useEffect(() => {
      // Without a saved one (e.g. an older flow), the default
      const saved =
        GRAPH_OPTIONS.find((o) => o.key === data?.graph_type) ??
        GRAPH_OPTIONS[0];
      setGraphType((prev) => (prev.key === saved.key ? prev : saved));
    }, [data?.graph_type]);
    // Called while replotting, to force the graph type some data needs. The
    // replot runs again when the graph type changes, so this must leave state
    // alone when that type is already selected; otherwise the plot redraws in
    // an endless loop (hundreds of times a second).
    const setForcedGraphType = (key: string) => {
      const nextGraphType =
        GRAPH_OPTIONS.find((o) => o.key === key) ?? GRAPH_OPTIONS[0];
      setGraphType((prev) =>
        prev.key === nextGraphType.key ? prev : nextGraphType,
      );
      return nextGraphType;
    };
    const [disableGraphTypeOption, setDisableGraphTypeOption] = useState(false);
    // The fewest values in any one box, violin or strip of the plot, if it
    // has them; below MIN_VALUES_FOR_DENSITY, violins and gradients are off
    const [fewestPerGroup, setFewestPerGroup] = useState<number | null>(null);
    const densityTooFew =
      fewestPerGroup !== null && fewestPerGroup < MIN_VALUES_FOR_DENSITY;
    // What's drawn: a box plot in place of a violin or gradient with too few
    // values, though that choice stays saved for when there are enough
    const shownGraphType =
      densityTooFew && isDensityGraph(graphType.key)
        ? GRAPH_OPTIONS.find((o) => o.key === "box") ?? graphType
        : graphType;

    const [placeholderText, setPlaceholderText] = useState(<></>);

    const [plotLegend, setPlotLegend] = useState<React.ReactNode>(null);
    const [selectedLegendItems] = useState<string[] | null>(null);

    // The MultiSelect so people can dynamically set what vars they care about
    const [multiSelectVars, setMultiSelectVars] = useState(data?.vars ?? []);
    const [multiSelectValue, setMultiSelectValue] = useState(
      data && Array.isArray(data.selected_vars) && data.selected_vars.length > 0
        ? data.selected_vars[0]
        : "LLM (default)",
    );

    // The x-axis, which are the names of eval results (if a dictionary)
    const [evalResVars, setEvalResVars] = useState<string[]>(
      data?.eval_res_vars ?? ["score"],
    );
    const [selectedEvalResVar, setSelectedEvalResVar] = useState(
      data?.selected_eval_res_var ?? "score",
    );
    const handleChangeSelectedEvalResVar = useCallback(
      (new_val: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedEvalResVar(new_val.target.value);
        if (id)
          setDataPropsForNode(id, {
            selected_eval_res_var: new_val.target.value,
          });
      },
      [id, setDataPropsForNode],
    );

    // The x-axis can also be a response stat (e.g. latency, or estimated
    // energy), so responses can be plotted without an evaluator. The stat
    // takes the place of the scores; responses without it are left out.
    const selectedStat = plottableStat(selectedEvalResVar);
    const statsView = useMemo(
      () =>
        selectedStat ? statsAsScores(inputResponses, selectedStat) : undefined,
      [inputResponses, selectedStat],
    );
    const responses = statsView?.responses ?? inputResponses;
    const metricName = selectedStat?.label ?? selectedEvalResVar;
    const isEnergy = selectedStat?.key === "__stat_est_energy_mwh";
    const isMeasuredEnergy = selectedStat?.key === "__stat_energy_mwh";
    const notesId = useId();

    // Why some responses aren't in a plot of a stat, for the note below it.
    // For energy, only the models EcoLogits doesn't cover are its doing;
    // other responses may just predate estimates, or lack a token count.
    const omittedNote = useMemo(() => {
      if (!statsView || statsView.kept === 0 || statsView.missing.length === 0)
        return undefined;
      const names = (rs: LLMResponse[]) =>
        Array.from(new Set(rs.map(llmNameOf))).join(", ");
      // Responses name their model by nickname: find what it is from the flow
      const specs = isEnergy ? modelSpecsByNickname() : undefined;
      const uncovered = specs
        ? statsView.missing.filter(
            (r) =>
              ecologitsCovers(
                typeof r.llm === "object" ? r.llm : specs.get(llmNameOf(r)),
              ) === false,
          )
        : [];
      const others = statsView.missing.filter((r) => !uncovered.includes(r));
      const sentences: string[] = [];
      if (uncovered.length > 0)
        sentences.push(
          `EcoLogits has no estimates for models ${names(uncovered)}, hence they are omitted here.`,
        );
      if (others.length > 0)
        sentences.push(
          isEnergy
            ? `Some responses from ${names(others)} have no estimate (for instance, ones collected before ChainForge estimated energy, or without a token count), hence they are omitted here.`
            : isMeasuredEnergy
              ? `Energy is measured only for local models (Ollama) on this machine, where ChainForge can read its energy counters (so far, Apple silicon Macs). Responses from ${names(others)} have no measurement, hence they are omitted here.`
              : `Some responses from ${names(others)} have no ${metricName.toLowerCase()}, hence they are omitted here.`,
        );
      return sentences.join(" ");
    }, [statsView, isEnergy, isMeasuredEnergy, metricName]);

    // Measured energy depends on the power settings it was measured under
    // (Low Power Mode, for one, uses less per token), so say when the
    // measurements plotted weren't all taken under the same ones
    const conditionsNote = useMemo(() => {
      if (!isMeasuredEnergy || !statsView) return undefined;
      const counts = new Map<string, number>();
      statsView.responses.forEach((r) =>
        r.stats?.forEach((s) => {
          const c = s?.energy_conditions;
          // Unknown (e.g. measured before ChainForge recorded them) is its
          // own group: not known to be comparable with the rest
          const key =
            describeConditions(
              c
                ? { power_source: c.power_source, power_mode: c.power_mode }
                : undefined,
            ) ?? "under unknown power settings";
          counts.set(key, (counts.get(key) ?? 0) + 1);
        }),
      );
      if (counts.size < 2) return undefined;
      const which = Array.from(counts.entries())
        .map(([conditions, n]) => `${n} ${conditions}`)
        .join("; ");
      return `These were measured under different power settings (${which}), which change the energy the same work takes. Compare them with care.`;
    }, [isMeasuredEnergy, statsView]);

    const notes: { text: string; details: string }[] = [];
    if (omittedNote)
      notes.push({
        text: isEnergy
          ? "Some estimates could not be shown."
          : isMeasuredEnergy
            ? "Some measurements could not be shown."
            : "Some values could not be shown.",
        details: omittedNote,
      });
    if (conditionsNote)
      notes.push({
        text: "Measured under different power settings.",
        details: conditionsNote,
      });

    // Typically, a user will only need the default LLM 'group' --all LLMs in responses.
    // However, when prompts are chained together, the original LLM info is stored in metavars as a key.
    // LLM groups allow you to plot against the original LLMs, even though a 'scorer' LLM might come after.
    const [availableLLMGroups, setAvailableLLMGroups] = useState(
      data?.llm_groups ?? [{ value: "LLM", label: "LLM" }],
    );
    const [selectedLLMGroup, setSelectedLLMGroup] = useState(
      data?.selected_llm_group ?? "LLM",
    );
    const handleChangeLLMGroup = useCallback(
      (new_val: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedLLMGroup(new_val.target.value);
        if (id)
          setDataPropsForNode(id, { selected_llm_group: new_val.target.value });
      },
      [id, setDataPropsForNode],
    );

    // When the user clicks an item in the drop-down,
    // we want to autoclose the multiselect drop-down:
    const multiSelectRef = useRef<HTMLSelectElement>(null);
    const handleMultiSelectValueChange = (
      new_val: React.ChangeEvent<HTMLSelectElement>,
    ) => {
      if (multiSelectRef?.current) {
        multiSelectRef.current.blur();
      }
      // setStatus(Status.LOADING);
      setMultiSelectValue(new_val.target.value);
      if (id)
        setDataPropsForNode(id, { selected_vars: [new_val.target.value] });
    };

    // Call this to reset the dropdowns and options for the user when the response format changes
    const resetControls = (resps: LLMResponse[]) => {
      if (resps.length === 0) return;

      // Find all vars in responses
      let varnames: string[] | Set<string> = new Set<string>();
      let metavars: string[] | Set<string> = new Set<string>();
      resps.forEach((resp_obj) => {
        Object.keys(resp_obj.vars).forEach((v) =>
          (varnames as Set<string>).add(v),
        );
        if (resp_obj.metavars)
          Object.keys(resp_obj.metavars).forEach((v) =>
            (metavars as Set<string>).add(v),
          );
      });
      varnames = Array.from(varnames);
      metavars = Array.from(metavars);

      // Find all keys in eval results, then any response stats to plot instead
      const eval_res_keys = findEvalResKeys(resps);
      plottableStatsIn(resps).forEach((stat) => eval_res_keys.add(stat.key));
      if (eval_res_keys.size === 0) eval_res_keys.add("score"); // default to 'score' if no keys found
      if (!eval_res_keys.has(selectedEvalResVar)) {
        // Default to the first in the list: a score if there are any, else a stat
        const first = eval_res_keys.values().next().value as string;
        setSelectedEvalResVar(first);
        if (id) setDataPropsForNode(id, { selected_eval_res_var: first });
      }

      // Get all vars for the y-axis dropdown, merging metavars and vars into one list,
      // and excluding any special 'LLM group' metavars:
      const msvars = [{ value: "LLM (default)", label: "LLM (default)" }]
        .concat(varnames.map((name) => ({ value: name, label: name })))
        .concat(
          metavars.filter(cleanMetavarsFilterFunc).map((name) => ({
            value: `__meta_${name}`,
            label: `${name} (meta)`,
          })),
        );

      // Find all the special metavars and vars and put them in the 'group by' dropdown:
      const available_llm_groups = [{ value: "LLM", label: "LLM" }]
        .concat(varnames.map((name) => ({ value: name, label: name })))
        .concat(
          metavars.filter(cleanMetavarsFilterFunc).map((name) => {
            return {
              value: `__meta_${name}`,
              label: `${name} (meta)`,
            };
          }),
        );
      if (available_llm_groups.some((g) => g.value.startsWith("__meta_llm_")))
        available_llm_groups[0] = { value: "LLM", label: "LLMs (last)" };
      setAvailableLLMGroups(available_llm_groups);

      // Check for a change in available parameters. The y-axis choice is reset
      // only when the y-axis options change: new x-axis options (e.g. a stat a
      // newly added model reports) leave it alone.
      if (
        !multiSelectVars ||
        !multiSelectValue ||
        !areSetsEqual(
          new Set(msvars.map((o) => o.value)),
          new Set(multiSelectVars.map((o) => o.value)),
        )
      ) {
        setMultiSelectValue("LLM (default)");
        setMultiSelectVars(msvars);
        if (id)
          setDataPropsForNode(id, {
            vars: msvars,
            selected_vars: [],
            llm_groups: available_llm_groups,
          });
      }
      if (!evalResVars || !areSetsEqual(new Set(evalResVars), eval_res_keys)) {
        setEvalResVars(Array.from(eval_res_keys));
        if (id)
          setDataPropsForNode(id, { eval_res_vars: Array.from(eval_res_keys) });
      }
    };

    // On init, run resetControls
    useEffect(() => {
      resetControls(inputResponses);
    }, []);

    const castData = (v: LLMResponseData) =>
      typeof v === "string" || typeof v === "number"
        ? StringLookup.get(v) ?? "(unknown lookup error)"
        : v.d;

    // Define functions accessible from the parent component
    useImperativeHandle(ref, () => ({
      resetControls,
    }));

    // Pending transitions display loading spinner
    useEffect(() => {
      if (!whenReplotting) return;
      whenReplotting(isPlotRerenderPending);
    }, [isPlotRerenderPending]);

    // Re-plot responses when any responses or settings change
    useEffect(() => {
      if (!multiSelectValue) return;
      if (selectedStat && inputResponses.length > 0 && responses.length === 0) {
        setPlotlySpec([]);
        setPlaceholderText(
          <p style={{ maxWidth: "220px", fontSize: "13px" }}>
            None of these responses have {metricName.toLowerCase()}.
          </p>,
        );
        return;
      }
      if (!responses || responses.length === 0) return;

      // Check if there are evaluation results
      if (responses.every((r) => r?.eval_res === undefined)) {
        setPlaceholderText(
          <p
            style={{
              maxWidth: "220px",
              backgroundColor: "#f0f0aa",
              padding: "10px",
              fontSize: "13px",
            }}
          >
            To plot evaluation results, you need to run LLM responses through an
            Evaluator Node or LLM Scorer Node first.
          </p>,
        );
        return;
      }

      startTransition(() => {
        // Set again below by the plots with boxes, violins or strips
        setFewestPerGroup(null);
        /**
         * The graph type to draw, given how many values each box, violin or
         * strip has: a box plot for a violin or gradient with too few.
         */
        const graphTypeToDraw = (groupSizes: number[]): string => {
          const nonEmpty = groupSizes.filter((n) => n > 0);
          const fewest = nonEmpty.length > 0 ? Math.min(...nonEmpty) : null;
          setFewestPerGroup(fewest);
          return isDensityGraph(graphType.key) &&
            fewest !== null &&
            fewest < MIN_VALUES_FOR_DENSITY
            ? "box"
            : graphType.key;
        };
        // How many values the responses matching `match` have
        const countValues = (
          rs: LLMResponse[],
          match: (r: LLMResponse) => boolean,
        ) =>
          rs.reduce(
            (n, r) => (match(r) ? n + get_items(r.eval_res).length : n),
            0,
          );

        const normalizeGroupBucket = (value: unknown): string => {
          if (value === undefined || value === null) return "(missing)";
          return llmResponseDataToString(value as LLMResponseData).trim();
        };

        const get_llm = (resp_obj: LLMResponse) => {
          if (selectedLLMGroup === "LLM") {
            if (
              typeof resp_obj.llm === "string" ||
              typeof resp_obj.llm === "number"
            ) {
              return StringLookup.get(resp_obj.llm) ?? String(resp_obj.llm);
            }
            return normalizeGroupBucket(resp_obj.llm?.name);
          } else if (selectedLLMGroup?.startsWith("__meta_")) {
            const meta_key = selectedLLMGroup.slice("__meta_".length);
            return normalizeGroupBucket(resp_obj.metavars?.[meta_key]);
          } else {
            return normalizeGroupBucket(resp_obj.vars[selectedLLMGroup]);
          }
        };
        const getLLMsInResponses = (responses: LLMResponse[]) =>
          getUniqueKeysInResponses(responses, get_llm);

        // Get all LLMs in responses, by selected LLM group
        const llm_names = getLLMsInResponses(responses);

        // Create Plotly spec here
        const varnames =
          multiSelectValue !== "LLM (default)" && multiSelectValue !== undefined
            ? [multiSelectValue]
            : [];
        let spec: Dict[] | Dict = [];
        const layout: Dict = {
          autosize: true,
          dragmode: "pan",
          title: "",
          margin: {
            l: 125,
            r: 0,
            b: 36,
            t: 20,
            pad: 6,
          },
          yaxis: {
            showgrid: true,
            color: colorScheme === "light" ? "#444" : "#ddd",
            // Widen the left margin to fit the labels as drawn: the margin
            // worked out below estimates their width from their length, which
            // falls short for wide letters ("gemma4:e4b" lost "ge")
            automargin: true,
          },
          // Make the plot background transparent
          paper_bgcolor: "rgba(0,0,0,0)",
          plot_bgcolor: "rgba(0,0,0,0)",
          xaxis: {
            color: colorScheme === "light" ? "#444" : "#ddd",
          },
        };

        // Bucket responses by LLM:
        const responses_by_llm: Dict<LLMResponse[]> = {};
        responses.forEach((item) => {
          const llm = get_llm(item);
          if (llm in responses_by_llm) responses_by_llm[llm].push(item);
          else responses_by_llm[llm] = [item];
        });

        // Get the type of evaluation results, if present
        // (This is assumed to be consistent across response batches)
        let typeof_eval_res =
          responses[0].eval_res && "dtype" in responses[0].eval_res
            ? responses[0].eval_res.dtype
            : "Numeric";

        let sel_typeof_eval_res = typeof_eval_res;
        if (typeof_eval_res.includes("KeyValue")) {
          const first_item = responses[0].eval_res?.items?.[0];
          if (typeof first_item === "object") {
            const val = first_item[selectedEvalResVar];
            if (typeof val === "boolean") {
              sel_typeof_eval_res = "Boolean";
            } else if (typeof val === "number") {
              sel_typeof_eval_res = "Numeric";
            } else if (typeof val === "string") {
              sel_typeof_eval_res = "Categorical";
            }
          }
        }

        // If categorical type, check if all binary:
        if (typeof_eval_res === "Categorical") {
          const is_all_bools = responses.reduce(
            (acc0: boolean, res_obj: LLMResponse) =>
              acc0 &&
              res_obj.eval_res !== undefined &&
              res_obj.eval_res.items?.reduce(
                (acc: boolean, cur: EvaluationScore) =>
                  acc && typeof cur === "boolean",
                true,
              ),
            true,
          );
          if (is_all_bools) {
            typeof_eval_res = "Boolean";
            sel_typeof_eval_res = "Boolean";
            setDisableGraphTypeOption(true);
          }
        } else {
          setDisableGraphTypeOption(false);
        }

        // Check the max length of eval results, as if it's only 1 score per item (num of generations per prompt n=1),
        // we might want to plot the result differently:
        let max_num_results_per_prompt = 1;
        responses.forEach((res_obj) => {
          if (
            res_obj.eval_res !== undefined &&
            res_obj.eval_res?.items?.length > max_num_results_per_prompt
          )
            max_num_results_per_prompt = res_obj.eval_res.items.length;
        });

        const plot_legend: React.ReactNode | null = null;
        let metric_axes_labels: string[] = [];
        if (selectedStat) metric_axes_labels = [metricName];
        else if (
          typeof_eval_res.includes("KeyValue") &&
          responses.some((r) => r.eval_res !== undefined)
        ) {
          metric_axes_labels = Array.from(findEvalResKeys(responses));
        }

        const get_var = (
          resp_obj: LLMResponse,
          varname: string,
          empty_str_if_undefined = false,
        ) => {
          const v = varname.startsWith("__meta_")
            ? resp_obj.metavars[varname.slice("__meta_".length)]
            : resp_obj.vars[varname];
          if (v === undefined && empty_str_if_undefined) return "";
          return llmResponseDataToString(v);
        };

        const get_var_and_trim = (
          resp_obj: LLMResponse,
          varname: string,
          empty_str_if_undefined = false,
        ) => {
          const v = get_var(resp_obj, varname, empty_str_if_undefined);
          if (v !== undefined) return v.trim();
          else return v;
        };

        // Hover text for each response: its text, after the stat's value when
        // plotting a stat. Per response, since one response can give several texts.
        const hoverTexts = (r: LLMResponse) => {
          const values = statsView?.hover.get(r);
          return r.responses.flatMap((resp, i) => {
            const texts = createHoverTexts([castData(resp)]);
            return values
              ? texts.map((t) => `<b>${values[i]}</b><br>${t}`)
              : texts;
          });
        };

        // A bar chart of a stat shows its mean per response, so models are
        // compared fairly however many responses each has (scores are summed)
        const barTotals = !selectedStat;
        const barTitle = selectedStat
          ? `Mean ${metricName.charAt(0).toLowerCase()}${metricName.slice(1)} per response`
          : undefined;

        const get_items = (eval_res_obj?: EvaluationResults) => {
          if (eval_res_obj === undefined) return [];
          if (typeof_eval_res.includes("KeyValue"))
            return eval_res_obj.items.map(
              (item) =>
                (item as Dict<boolean | number | string>)[selectedEvalResVar],
            );
          return eval_res_obj.items;
        };

        // Only for Boolean data
        const plot_accuracy = (
          resp_to_x: (r: LLMResponse) => string,
          group_type: "var" | "llm",
        ) => {
          // Plots the percentage of 'true' evaluations out of the total number of evaluations,
          // per category of 'resp_to_x', as a horizontal bar chart, with different colors per category.
          const names = new Set(responses.map(resp_to_x));
          const shortnames = genUniqueShortnames(names);
          const x_items: number[] = [];
          const y_items: string[] = [];
          const marker_colors: string[] = [];
          // const error_values: number[][] = [];
          for (const name of names) {
            // Add a shortened version of the name as the y-tick
            y_items.push(shortnames[name]);

            // Calculate the number of true values over the total possible number
            let num_true_vals = 0;
            let num_eval_scores = 0;
            const all_samples: number[] = [];
            for (const r of responses) {
              if (resp_to_x(r) !== name) continue;
              const items = get_items(r.eval_res);
              Array.prototype.push.apply(
                all_samples,
                items.map((i) => (i === true ? 1 : 0)),
              ); // extend the `all_samples` array
              num_eval_scores += items.length;
              num_true_vals += items.filter((res) => res === true).length;
            }
            if (num_eval_scores > 0)
              x_items.push(num_true_vals * (100 / num_eval_scores));

            // Compute error bar info
            // error_values.push(computeErrorBar(all_samples, 100));

            // Lookup the color per LLM when displaying LLM differences,
            // otherwise use the palette for displaying variables.
            const color =
              group_type === "llm"
                ? getColorForLLMAndSetIfNotFound(name)
                : getColorForLLMAndSetIfNotFound(get_llm(responses[0]));
            marker_colors.push(color);
          }

          // Set the left margin to fit the yticks labels
          layout.margin.l = calcLeftPaddingForYLabels(
            Object.values(shortnames),
          );

          spec = [
            {
              type: "bar",
              y: y_items,
              x: x_items,
              marker: {
                color: marker_colors,
              },
              // error_x: {
              //   type: "data",
              //   // Asymmetric errors bars, since we're using bootstrapping to determine the 95% CI
              //   array: error_values.map((e) => e[1]), // Upper bound
              //   arrayminus: error_values.map((e) => e[0]), // Lower bound
              //   visible: true,
              // },
              hovertemplate: "%{x:.2f}%<extra>%{y}</extra>",
              showtrace: false,
              orientation: "h",
            },
          ];
          layout.xaxis = {
            range: [0, 100],
            tickmode: "linear",
            tick0: 0,
            dtick: 10,
            ...layout.xaxis,
          };

          setForcedGraphType("bar"); // bar chart

          if (metric_axes_labels.length > 0)
            layout.xaxis = {
              title: { font: { size: 12 }, text: metricName },
              ...layout.xaxis,
            };
          else
            layout.xaxis = {
              title: { font: { size: 12 }, text: "% percent true" },
              ...layout.xaxis,
            };
        };

        const plot_simple_boxplot = (
          resp_to_x: (r: LLMResponse) => string,
          group_type: "var" | "llm",
        ) => {
          let names = new Set<string>();
          const plotting_categorical_vars =
            group_type === "var" && sel_typeof_eval_res === "Categorical";

          // When we're plotting vars, we want the stacked bar colors to be the *categories*,
          // and the x_items to be the names of vars, so that the left axis is a vertical list of varnames.
          if (plotting_categorical_vars) {
            // Get all categories present in the evaluation results
            responses.forEach((r) =>
              get_items(r.eval_res).forEach((i) => names.add(i.toString())),
            );
          } else {
            // Get all possible values of the single variable response ('name' vals)
            names = new Set(responses.map(resp_to_x));
          }

          const shortnames = genUniqueShortnames(names);
          const yLabelShortnames = genUniqueShortnames(
            new Set(responses.map(resp_to_x)),
          );
          // For categorical and boolean scores, the y axis lists the score
          // values themselves, so they need shortening (and line breaks) too.
          const scoreShortnames = genUniqueShortnames(
            new Set(
              responses.flatMap((r) =>
                get_items(r.eval_res).map((i) => i?.toString() ?? ""),
              ),
            ),
          );
          // What ends up on the y axis, whichever branch below runs: the left
          // margin has to fit these, not the series names.
          const yTickLabels = new Set<string>();
          // For a density gradient: its strips, one row each, drawn after the loop
          const gradientBands: DensityBand[] = [];
          const plotKey =
            sel_typeof_eval_res === "Boolean" ||
            sel_typeof_eval_res === "Categorical"
              ? graphType.key
              : graphTypeToDraw(
                  Array.from(names).map((name) =>
                    countValues(responses, (r) => resp_to_x(r) === name),
                  ),
                );
          for (const name of names) {
            let x_items: EvaluationScore[] = [];
            let text_items: string[] = [];

            if (plotting_categorical_vars) {
              responses.forEach((r) => {
                // Get all evaluation results for this response which match the category 'name':
                const eval_res = get_items(r.eval_res).filter(
                  (i) => i === name,
                );
                const rawLabel = resp_to_x(r);
                const yLabel = yLabelShortnames[rawLabel] ?? rawLabel;
                x_items = x_items.concat(
                  new Array(eval_res.length).fill(yLabel),
                );
              });
            } else {
              responses.forEach((r) => {
                if (resp_to_x(r) !== name) return;
                x_items = x_items.concat(get_items(r.eval_res));
                text_items = text_items.concat(hoverTexts(r));
              });
            }

            // Lookup the color per LLM when displaying LLM differences,
            // otherwise use the palette for displaying variables.
            const color =
              group_type === "llm"
                ? getColorForLLMAndSetIfNotFound(name)
                : // :   varcolors[name_idx % varcolors.length];
                  getColorForLLMAndSetIfNotFound(get_llm(responses[0]));

            if (
              sel_typeof_eval_res === "Boolean" ||
              sel_typeof_eval_res === "Categorical"
            ) {
              // Plot a histogram for categorical or boolean data.
              const y_labels = x_items.map((v) => {
                const label = v?.toString() ?? "";
                return plotting_categorical_vars
                  ? label
                  : scoreShortnames[label] ?? label;
              });
              y_labels.forEach((l) => yTickLabels.add(l));
              spec.push({
                type: "histogram",
                histfunc: "sum",
                name: shortnames[name],
                marker: { color },
                y: y_labels,
                orientation: "h",
              });
              layout.barmode = "stack";
              layout.yaxis = {
                // Keep what the base layout set, e.g. the axis colour for the
                // current theme: dropping it leaves Plotly's gray, which is
                // hard to read in dark mode.
                ...layout.yaxis,
                showticklabels: true,
                dtick: 1,
                type: "category",
                showgrid: true,
              };
              layout.xaxis = {
                title: {
                  font: { size: 12 },
                  text:
                    metric_axes_labels.length > 0
                      ? `Number of scores (${metricName})`
                      : "Number of scores",
                },
                ...layout.xaxis,
              };
            } else {
              // Plot bar or boxplots for all other cases.
              const d: Dict = {
                name: shortnames[name],
                x: x_items,
                text: text_items,
                hovertemplate: "%{text}",
                orientation: "h",
                marker: { color },
              };

              // If only one result, plot a bar chart:
              if (x_items.length === 1) {
                d.type = "bar";
                d.textposition = "none"; // hide the text which appears within each bar
                d.y = new Array(x_items.length).fill(shortnames[name]);
                yTickLabels.add(shortnames[name]);
                setForcedGraphType("bar");
              } else {
                // If multiple eval results per response object (num generations per prompt n > 1),
                // let user decide:
                if (plotKey === "bar") {
                  d.type = "histogram";
                  d.histfunc = barTotals ? "sum" : "avg";
                  d.y = new Array(x_items.length).fill(shortnames[name]);
                  d.textposition = "none"; // hide the text which appears within each bar
                  const xaxis_title =
                    barTitle ??
                    (metric_axes_labels.length > 0
                      ? "Sum of '" + metricName + "'"
                      : "Sum of scores");
                  layout.xaxis = {
                    title: { font: { size: 12 }, text: xaxis_title },
                    ...layout.xaxis,
                  };

                  // Compute error bars if present
                  // const error_values = [
                  //   computeErrorBar(x_items.map(castEvalScoreToNum), 1.0, sum),
                  // ];
                  // if (error_values.length > 0)
                  //   d.error_x = {
                  //     type: "data",
                  //     // Asymmetric errors bars, since we're using bootstrapping to determine the 95% CI
                  //     array: error_values.map((e) => e[1]), // Upper bound
                  //     arrayminus: error_values.map((e) => e[0]), // Lower bound
                  //     visible: true,
                  //   };
                } else if (plotKey === "gradient") {
                  gradientBands.push({
                    y: gradientBands.length,
                    halfHeight: 0.35,
                    values: x_items.map(castEvalScoreToNum),
                    texts: text_items,
                    title: shortnames[name],
                    color,
                  });
                  continue;
                } else {
                  // Box-and-whiskers plot, or a violin
                  d.type = "box";
                  d.boxpoints = "all";
                  d.hoveron = "points";
                  if (plotKey === "violin") asViolin(d);
                }
              }

              spec.push(d);
              if (d.type === "box" || d.type === "violin") {
                const summary = boxSummaryTrace(
                  [
                    {
                      y: shortnames[name],
                      title: shortnames[name],
                      values: x_items.map(castEvalScoreToNum),
                    },
                  ],
                  color,
                );
                if (summary) spec.push(summary);
              }
            }
          }
          if (gradientBands.length > 0) {
            spec.push(
              ...densityStripTraces(gradientBands, colorScheme !== "light"),
            );
            setDensityStripAxes(
              layout,
              gradientBands.map((b) => b.title),
            );
          }
          layout.hovermode = "closest";
          layout.showlegend = false;

          // Set the left margin to fit the yticks labels. Which labels those
          // are depends on the branch above: the score values, the variable's
          // values, or the series names.
          layout.margin.l = calcLeftPaddingForYLabels(
            yTickLabels.size > 0
              ? Array.from(yTickLabels)
              : Object.values(
                  plotting_categorical_vars ? yLabelShortnames : shortnames,
                ),
          );

          if (metric_axes_labels.length > 0)
            layout.xaxis = {
              title: { font: { size: 12 }, text: metricName },
              ...layout.xaxis,
            };
        };

        const plot_grouped_boxplot = (
          resp_to_x: (r: LLMResponse) => string,
        ) => {
          // Get all possible values of the single variable response ('name' vals)
          const names = new Set(responses.map(resp_to_x));
          const shortnames = genUniqueShortnames(names);
          // For a density gradient: a row per value of the variable, split
          // into a strip per model (first model at the top), drawn after the loop
          const rowLabels = Object.values(shortnames);
          const gradientBands: DensityBand[] = [];
          const stripHeight = 0.8 / Math.max(1, llm_names.length);
          const plotKey = graphTypeToDraw(
            llm_names.flatMap((llm) =>
              Array.from(names).map((name) =>
                countValues(
                  responses_by_llm[llm],
                  (r) => resp_to_x(r) === name,
                ),
              ),
            ),
          );

          llm_names.forEach((llm, llmIdx) => {
            // Create HTML for hovering over a single datapoint. We must use 'br' to specify line breaks.
            const rs = responses_by_llm[llm];

            let x_items: EvaluationScore[] = [];
            let y_items: EvaluationScore[] = [];
            // let x_items_by_shortname: { [key: string]: [] } = {};
            let text_items: string[] = [];
            for (const name of names) {
              rs.forEach((r) => {
                if (resp_to_x(r) !== name) return;
                const items = get_items(r.eval_res);
                x_items = x_items.concat(items).flat();
                text_items = text_items.concat(hoverTexts(r)).flat();
                y_items = y_items
                  .concat(Array(items.length).fill(shortnames[name]))
                  .flat();
              });
            }

            if (sel_typeof_eval_res === "Boolean") {
              // Plot a histogram for boolean (true/false) categorical data.
              spec.push({
                type: "histogram",
                histfunc: "sum",
                name: llm,
                marker: { color: getColorForLLMAndSetIfNotFound(llm) },
                x: x_items.map((i) => (i === true ? "1" : "0")),
                y: y_items,
                orientation: "h",
              });
              layout.barmode = "stack";
              layout.xaxis = {
                title: { font: { size: 12 }, text: "Number of 'true' values" },
                ...layout.xaxis,
              };
              setForcedGraphType("bar");
            } else if (plotKey === "gradient") {
              const color = getColorForLLMAndSetIfNotFound(llm);
              rowLabels.forEach((label, row) => {
                const idxs = y_items
                  .map((y, idx) => (y === label ? idx : -1))
                  .filter((idx) => idx >= 0);
                gradientBands.push({
                  y: row + 0.4 - (llmIdx + 0.5) * stripHeight,
                  halfHeight: stripHeight * 0.45,
                  values: idxs.map((idx) => castEvalScoreToNum(x_items[idx])),
                  texts: idxs.map((idx) => text_items[idx]),
                  title: `${llm} · ${label}`,
                  color,
                });
              });
              // A legend entry for the model, in its colour
              spec.push({
                type: "scatter",
                mode: "markers",
                x: [null],
                y: [null],
                name: llm,
                marker: { color, symbol: "square", size: 10 },
                hoverinfo: "skip",
              });
              layout.xaxis = {
                title: { font: { size: 12 }, text: "score" },
                ...layout.xaxis,
              };
            } else {
              // Plot a boxplot or bar chart for other cases.
              const d = {
                name: llm,
                marker: { color: getColorForLLMAndSetIfNotFound(llm) },
                x: x_items,
                y: y_items,
                boxpoints: "all",
                text: text_items,
                hovertemplate: "%{text} <b><i>(%{x})</i></b>",
                orientation: "h",
              } as Dict;

              // If only one result, plot a bar chart:
              // if (max_num_results_per_prompt === 1) {
              let xaxis_title = "score";
              if (plotKey === "bar") {
                d.type = "bar";
                d.textposition = "none"; // hide the text which appears within each bar
                xaxis_title =
                  barTitle ??
                  (metric_axes_labels.length > 0
                    ? "Sum of '" + metricName + "'"
                    : "Sum of scores");

                if (sel_typeof_eval_res === "Numeric") {
                  // To make error bars work, we need to sum the numbers, instead of relying
                  // upon the stacked bar chart:
                  let sum_x_items: number[] = [];
                  // let error_bars: number[][] = [];
                  const seq_y_items = [];
                  for (const name of Object.values(shortnames)) {
                    seq_y_items.push(name);
                    const xs_for_y = x_items
                      .filter((_, idx) => y_items[idx] === name)
                      .map(castEvalScoreToNum);
                    sum_x_items = sum_x_items.concat(
                      barTotals ? sum(xs_for_y) : mean(xs_for_y),
                    );
                    // error_bars = error_bars.concat([
                    //   computeErrorBar(xs_for_y, 1.0, sum),
                    // ]);
                  }
                  d.x = sum_x_items;
                  d.y = seq_y_items;
                  d.hovertemplate = llm;
                  delete d.text;

                  // Add error bars to plot
                  // d.error_x = {
                  //   type: "data",
                  //   // Asymmetric errors bars, since we're using bootstrapping to determine the 95% CI
                  //   array: error_bars.map((e) => e[1]), // Upper bound
                  //   arrayminus: error_bars.map((e) => e[0]), // Lower bound
                  //   visible: true,
                  // };
                }
              } else {
                // Box-and-whiskers plot. Hovering the box itself would label
                // each of its stats separately (see boxSummaryTrace)
                d.type = "box";
                d.hoveron = "points";
                d.offsetgroup = llm;
                if (plotKey === "violin") asViolin(d);
              }

              spec.push(d);
              if (d.type === "box" || d.type === "violin") {
                const summary = boxSummaryTrace(
                  Object.values(shortnames).map((y) => ({
                    y,
                    title: `${llm} · ${y}`,
                    values: x_items
                      .filter((_, idx) => y_items[idx] === y)
                      .map(castEvalScoreToNum),
                  })),
                  getColorForLLMAndSetIfNotFound(llm),
                  llm,
                );
                if (summary) {
                  spec.push(summary);
                  layout.scattermode = "group";
                }
              }
              layout.xaxis = {
                title: { font: { size: 12 }, text: xaxis_title },
                ...layout.xaxis,
              };
            }
          });
          if (gradientBands.length > 0) {
            spec.push(
              ...densityStripTraces(gradientBands, colorScheme !== "light"),
            );
            setDensityStripAxes(layout, rowLabels);
          }
          layout.boxmode = "group";
          layout.violinmode = "group";
          layout.bargap = 0.5;

          // Set the left margin to fit the yticks labels
          layout.margin.l = calcLeftPaddingForYLabels(
            Object.values(shortnames),
          );

          if (metric_axes_labels.length > 0)
            layout.xaxis = {
              title: { font: { size: 12 }, text: metricName },
              ...layout.xaxis,
            };
        };

        // PARALLEL COORDINATES PLOT -- Disabled for now.
        // May be re-enabled in the future.
        // if (num_metrics > 1) {
        //   // For 2 or more metrics, display a parallel coordinates plot.
        //   // :: For instance, if evaluator produces { height: 32, weight: 120 } plot responses with 2 metrics, 'height' and 'weight'
        //   if (varnames.length === 1) {
        //     const unique_vals = getUniqueKeysInResponses(
        //       responses,
        //       (resp_obj) => get_var(resp_obj, varnames[0]),
        //     );
        //     // const response_txts = responses.map(res_obj => res_obj.responses).flat();

        //     const group_colors = varcolors;
        //     const unselected_line_color = "#ddd";
        //     const spec_colors = responses
        //       .map((resp_obj) => {
        //         const idx = unique_vals.indexOf(get_var(resp_obj, varnames[0]));
        //         return resp_obj.eval_res
        //           ? Array(resp_obj.eval_res.items.length).fill(idx)
        //           : [];
        //       })
        //       .flat();

        //     const colorscale: [number, string][] = [];
        //     for (let i = 0; i < unique_vals.length; i++) {
        //       if (
        //         !selectedLegendItems ||
        //         selectedLegendItems.indexOf(unique_vals[i]) > -1
        //       )
        //         colorscale.push([
        //           i / (unique_vals.length - 1),
        //           group_colors[i % group_colors.length],
        //         ]);
        //       else
        //         colorscale.push([
        //           i / (unique_vals.length - 1),
        //           unselected_line_color,
        //         ]);
        //     }

        //     const dimensions: Dict = [];
        //     metric_axes_labels.forEach((metric) => {
        //       const evals = extractEvalResultsForMetric(metric, responses);
        //       dimensions.push({
        //         range: evals.every((e) => typeof e === "number")
        //           ? [
        //               Math.min(...(evals as number[])),
        //               Math.max(...(evals as number[])),
        //             ]
        //           : undefined,
        //         label: metric,
        //         values: evals,
        //       });
        //     });

        //     spec.push({
        //       type: "parcoords",
        //       pad: [10, 10, 10, 10],
        //       line: {
        //         color: spec_colors,
        //         colorscale,
        //       },
        //       dimensions,
        //     });
        //     layout.margin = { l: 40, r: 40, b: 40, t: 50, pad: 0 };
        //     layout.paper_bgcolor = "white";
        //     layout.font = { color: "black" };
        //     layout.selectedpoints = [];

        //     // There's no built-in legend for parallel coords, unfortunately, so we need to construct our own:
        //     const legend_labels: Dict<string> = {};
        //     unique_vals.forEach((v, idx) => {
        //       if (!selectedLegendItems || selectedLegendItems.indexOf(v) > -1)
        //         legend_labels[v] = group_colors[idx % group_colors.length];
        //       else legend_labels[v] = unselected_line_color;
        //     });
        //     const onClickLegendItem = (label: string) => {
        //       if (
        //         selectedLegendItems &&
        //         selectedLegendItems.length === 1 &&
        //         selectedLegendItems[0] === label
        //       )
        //         setSelectedLegendItems(null); // Clicking twice on a legend item deselects it and displays all
        //       else setSelectedLegendItems([label]);
        //     };
        //     plot_legend = (
        //       <PlotLegend
        //         labels={legend_labels}
        //         onClickLabel={onClickLegendItem}
        //       />
        //     );

        //     // Tried to support Plotly hover events here, but looks like
        //     // currently there are unsupported for parcoords: https://github.com/plotly/plotly.js/issues/3012
        //     // onHover = (e) => {
        //     //     console.log(e.curveNumber);
        //     //     // const curveIdx = e.curveNumber;
        //     //     // if (curveIdx < response_txts.length) {
        //     //     //     if (!selectedLegendItems || selectedLegendItems.indexOf(unique_vals[spec_colors[curveIdx]]) > -1)
        //     //     //         console.log(response_txts[curveIdx]);
        //     //     // }
        //     // };
        //   } else {
        //     setSelectedLegendItems(null);
        //     const error_text =
        //       "Plotting evaluations with more than one metric and more than one prompt parameter is currently unsupported.";
        //     setPlaceholderText(
        //       <p
        //         style={{
        //           maxWidth: "220px",
        //           backgroundColor: "#f0aaaa",
        //           padding: "10px",
        //           fontSize: "13px",
        //         }}
        //       >
        //         {error_text}
        //       </p>,
        //     );
        //     console.error(error_text);
        //   }
        // } else {

        // A single metric --use plots like grouped box-and-whiskers, 3d scatterplot
        if (varnames.length === 0) {
          // No variables means they used a single prompt (no template) to generate responses
          // (Users are likely evaluating differences in responses between LLMs)
          if (sel_typeof_eval_res === "Boolean") plot_accuracy(get_llm, "llm");
          else plot_simple_boxplot(get_llm, "llm");
        } else if (varnames.length === 1) {
          // 1 var; numeric eval
          if (llm_names.length === 1) {
            if (sel_typeof_eval_res === "Boolean")
              // Accuracy plot per value of the selected variable:
              plot_accuracy((r) => get_var_and_trim(r, varnames[0]), "var");
            else {
              // Simple box plot, as there is only a single LLM in the response
              plot_simple_boxplot(
                (r) => get_var_and_trim(r, varnames[0]),
                "var",
              );
            }
          } else {
            // There are multiple LLMs in the response; do a grouped box plot by LLM.
            // Note that 'name' is now the LLM, and 'x' stores the value of the var:
            plot_grouped_boxplot((r) => get_var_and_trim(r, varnames[0]));
          }
        } else if (varnames.length === 2) {
          // Input is 2 vars; numeric eval
          // Display a 3D scatterplot with 2 dimensions:

          const names_0 = new Set(
            responses.map((r) => get_var_and_trim(r, varnames[0])),
          );
          const shortnames_0 = genUniqueShortnames(names_0);
          const names_1 = new Set(
            responses.map((r) => get_var_and_trim(r, varnames[1])),
          );
          const shortnames_1 = genUniqueShortnames(names_1);

          if (llm_names.length === 1) {
            spec = {
              type: "scatter3d",
              x: responses
                .map((r) => get_var(r, varnames[0], true))
                .map((s) => shortnames_0[s]),
              y: responses
                .map((r) => get_var(r, varnames[1], true))
                .map((s) => shortnames_1[s]),
              z: responses.map(
                (r) =>
                  get_items(r.eval_res).reduce(
                    (acc: number, val) =>
                      acc + (typeof val === "number" ? val : 0),
                    0,
                  ) / (r.eval_res?.items.length ?? 1),
              ), // calculates mean
              mode: "markers",
              marker: {
                color: getColorForLLMAndSetIfNotFound(llm_names[0]),
              },
            };
          } else {
            spec = [];
            llm_names.forEach((llm) => {
              const resps = responses.filter((r) => get_llm(r) === llm);
              spec.push({
                type: "scatter3d",
                x: resps
                  .map((r) => get_var(r, varnames[0], true))
                  .map((s) => shortnames_0[s]),
                y: resps
                  .map((r) => get_var(r, varnames[1], true))
                  .map((s) => shortnames_1[s]),
                z: resps.map(
                  (r) =>
                    get_items(r.eval_res).reduce(
                      (acc: number, val) =>
                        acc + (typeof val === "number" ? val : 0),
                      0,
                    ) / (r.eval_res?.items.length ?? 1),
                ), // calculates mean
                mode: "markers",
                marker: {
                  color: getColorForLLMAndSetIfNotFound(llm),
                },
                name: llm,
              });
            });
          }
        }

        if (!Array.isArray(spec)) spec = [spec];

        setPlotLegend(plot_legend);
        setPlotlySpec(spec as Dict[]);
        setPlotlyLayout(layout);
      });

      // if (plotDivRef && plotDivRef.current) {
      //     plotDivRef.current.style.width = '300px';
      // }
    }, [
      multiSelectVars,
      multiSelectValue,
      evalResVars,
      selectedEvalResVar,
      selectedLLMGroup,
      responses,
      statsView,
      selectedLegendItems,
      plotDivRef,
      // By key, so only a real change of graph type replots.
      graphType.key,
      colorScheme,
    ]);

    // Resize the plot when the div around it is resized (e.g. with the resize
    // handle). One observer for the div's lifetime, which resizes the plot only
    // when the div's size actually changed, and never while the plot is empty
    // or hidden (Plotly throws "Resize must be passed a displayed plot div").
    // Previously a new observer was added on every replot and never removed.
    const plotlySpecRef = useRef(plotlySpec);
    plotlySpecRef.current = plotlySpec;

    /**
     * Fits the plot to its div, when there's a plot and it's on screen.
     *
     * The plot is told the size to draw at, rather than asked to resize
     * itself: Plotly.Plots.resize measures the plot's own div, which has no
     * width or height of its own here, so it simply matches the plot and
     * nothing changes. The div that carries a size is the one the resize
     * handle drags.
     */
    const fitPlotToDiv = useCallback(() => {
      const elem = plotDivRef.current;
      // The plot's div (react-plotly's `el`), resized only while displayed.
      const gd = (plotlyRef.current as unknown as { el?: HTMLElement } | null)
        ?.el;
      if (
        !elem ||
        !gd ||
        plotlySpecRef.current.length === 0 ||
        elem.offsetWidth === 0 ||
        gd.offsetParent === null
      )
        return;
      const width = elem.clientWidth;
      const height = elem.clientHeight;
      if (width === 0 || height === 0) return;
      const full = (gd as unknown as { _fullLayout?: Dict })._fullLayout;
      if (full && full.width === width && full.height === height) return;
      Promise.resolve(Plotly.relayout(gd, { width, height })).catch(
        () => undefined,
      );
    }, []);

    // The div the resize handle drags, watched for size changes below. It's
    // state, not just a ref, so that attaching the observer is an effect that
    // re-runs if the div is replaced -- and whose cleanup is its own. (As a
    // ref callback, with the cleanup in a separate effect, StrictMode's
    // double-mount in development disconnected the observer for good: the
    // effect's cleanup ran, but React doesn't call ref callbacks again.)
    const [plotDiv, setPlotDiv] = useState<HTMLDivElement | null>(null);
    const setPlotDivRef = useCallback((elem: HTMLDivElement | null) => {
      plotDivRef.current = elem;
      setPlotDiv(elem);
    }, []);
    // The size it was last resized to, saved with the node: when it loads,
    // and whenever the node's data changes (e.g. a flow loaded in its place)
    const savedWidth = data?.plot_size?.width;
    const savedHeight = data?.plot_size?.height;
    useEffect(() => {
      if (!plotDiv) return;
      if (!savedWidth || !savedHeight) {
        // None saved (e.g. an older flow): the default size, not one left
        // over from data shown before
        plotDiv.style.width = "";
        plotDiv.style.height = "";
        return;
      }
      if (
        plotDiv.offsetWidth === savedWidth &&
        plotDiv.offsetHeight === savedHeight
      )
        return;
      plotDiv.style.width = `${savedWidth}px`;
      plotDiv.style.height = `${savedHeight}px`;
    }, [plotDiv, savedWidth, savedHeight]);
    const savePlotSize = useCallback(() => {
      const el = plotDivRef.current;
      if (id && el)
        setDataPropsForNode(id, {
          plot_size: { width: el.offsetWidth, height: el.offsetHeight },
        });
    }, [id, setDataPropsForNode]);
    useEffect(() => {
      if (!plotDiv || !window.ResizeObserver) return;
      let lastSize = "";
      const observer = new window.ResizeObserver((entries) => {
        const { width, height } = entries[0].contentRect;
        const size = `${Math.round(width)}x${Math.round(height)}`;
        if (size === lastSize) return;
        lastSize = size;
        fitPlotToDiv();
      });
      observer.observe(plotDiv);
      return () => observer.disconnect();
    }, [plotDiv, fitPlotToDiv]);

    // A plot is first drawn while its div is empty (and so hidden), where
    // Plotly falls back to its default 700x450. The div's size doesn't change
    // when the data arrives, so the observer above wouldn't fire: fit the new
    // plot to the div here instead, once it has rendered.
    useEffect(() => {
      if (plotlySpec.length === 0) return;
      const id = requestAnimationFrame(fitPlotToDiv);
      return () => cancelAnimationFrame(id);
    }, [plotlySpec, plotlyLayout, fitPlotToDiv]);

    // Bar chart or box plot, for data that can be shown either way: a button
    // with its name, in the toolbar or (compact) in the node's header
    const graphTypeMenu = (
      <Menu
        shadow="md"
        width={200}
        withArrow
        withinPortal
        disabled={disableGraphTypeOption}
      >
        <Menu.Target>
          {headerSlot ? (
            <Button
              className="nodrag"
              variant="outline"
              size="xs"
              compact
              color="gray"
              leftIcon={shownGraphType.icon}
              disabled={disableGraphTypeOption}
              // The same height and line as the header's other buttons
              // (inline, since Mantine's compact size sets its own height)
              style={{
                height: 20,
                marginTop: "-7px",
                marginRight: "4px",
                position: "relative",
                top: 5,
              }}
            >
              {shownGraphType.label}
            </Button>
          ) : (
            <Button
              variant="outline"
              size="xs"
              color="gray"
              leftIcon={shownGraphType.icon}
              disabled={disableGraphTypeOption}
            >
              {shownGraphType.label}
            </Button>
          )}
        </Menu.Target>

        <Menu.Dropdown>
          {GRAPH_OPTIONS.map((option) => (
            <Menu.Item
              key={option.key}
              icon={option.icon}
              disabled={densityTooFew && isDensityGraph(option.key)}
              onClick={() => {
                setGraphType(option);
                if (id) setDataPropsForNode(id, { graph_type: option.key });
              }}
            >
              {option.label}
              {densityTooFew && isDensityGraph(option.key) && (
                <div style={{ fontSize: 11 }}>
                  Needs {MIN_VALUES_FOR_DENSITY}+ scores per group; the smallest
                  has {fewestPerGroup}
                </div>
              )}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    );

    return (
      <>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            // As wide as the plot below makes the node, but no wider: without
            // this, the controls in one row set the node's width, and the
            // plot can't be resized narrower than them. This way they wrap.
            width: 0,
            minWidth: "100%",
            rowGap: "4px",
            margin: wideFormat ? "6pt 0 6pt 0" : undefined,
          }}
        >
          <div style={{ display: "inline-flex", maxWidth: "50%" }}>
            <span style={smallTextStyle}>y-axis:</span>
            <NativeSelect
              ref={multiSelectRef}
              onChange={handleMultiSelectValueChange}
              className="nodrag nowheel"
              data={multiSelectVars}
              placeholder="Pick param to plot"
              size="xs"
              value={multiSelectValue}
              miw="80px"
            />
          </div>
          <div
            style={{
              display: "inline-flex",
              justifyContent: "space-evenly",
              maxWidth: "30%",
              marginLeft: "10pt",
            }}
          >
            <span style={smallTextStyle}>x-axis:</span>
            <NativeSelect
              className="nodrag nowheel"
              data={evalResVars.map((v) => ({
                value: v,
                label: plottableStat(v)?.label ?? v,
              }))}
              size="xs"
              value={selectedEvalResVar}
              onChange={handleChangeSelectedEvalResVar}
              miw="80px"
            />
          </div>
          {availableLLMGroups && availableLLMGroups.length > 1 ? (
            <div
              style={{
                display: "inline-flex",
                justifyContent: "space-evenly",
                maxWidth: "30%",
                marginLeft: "10pt",
              }}
            >
              <span style={smallTextStyle}>group by:</span>
              <NativeSelect
                className="nodrag nowheel"
                onChange={handleChangeLLMGroup}
                data={availableLLMGroups}
                size="xs"
                value={selectedLLMGroup}
                miw="80px"
                disabled={availableLLMGroups.length <= 1}
              />
            </div>
          ) : (
            <></>
          )}
          {headerSlot ? (
            // In the node's header instead (see VisNode), to leave the
            // toolbar narrow enough for the plot to be resized
            createPortal(graphTypeMenu, headerSlot)
          ) : (
            <div
              style={{
                display: "inline-flex",
                justifyContent: "end",
                maxWidth: "30%",
                marginLeft: "10pt",
              }}
            >
              {graphTypeMenu}
            </div>
          )}
        </div>
        {!wideFormat && <hr />}
        <div
          className="nodrag"
          ref={setPlotDivRef}
          style={{
            minWidth: "150px",
            minHeight: "100px",
            position: "relative",
          }}
        >
          {plotlySpec && plotlySpec.length > 0 ? <></> : placeholderText}
          <Plot
            ref={plotlyRef}
            data={plotlySpec}
            layout={plotlyLayout}
            // Not react-plotly's window resize handler: it resizes plots even
            // while hidden (e.g. in a closed inspector), which Plotly rejects.
            // The ResizeObserver above resizes the plot instead.
            useResizeHandler={false}
            className="plotly-vis"
            style={{
              display: plotlySpec && plotlySpec.length > 0 ? "block" : "none",
              // border: wideFormat ? "1px solid #bbb" : "none",
              // paddingBottom: wideFormat ? "6pt" : "0",
            }}
          />
          {plotLegend ?? <></>}
          <ResizeHandle
            targetRef={plotDivRef}
            minWidth={150}
            minHeight={100}
            onResizeEnd={savePlotSize}
          />
        </div>
        {/* Outside the plot's div: the plot resizes to fill that div, so
            anything else in it would make the plot grow without end. */}
        {notes.map((note, i) => (
          <React.Fragment key={i}>
            <Tooltip
              label={note.details}
              multiline
              width={260}
              withArrow
              withinPortal
              position="bottom-start"
              // Keyboard users reach the details by focusing the note
              events={{ hover: true, focus: true, touch: true }}
            >
              <div
                tabIndex={0}
                aria-describedby={`${notesId}-${i}`}
                style={{
                  ...smallTextStyle,
                  marginTop: "4px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: "help",
                  width: "fit-content",
                }}
              >
                <IconAlertTriangle
                  size={14}
                  color="#e8a33d"
                  style={{ flexShrink: 0 }}
                />
                {note.text}
              </div>
            </Tooltip>
            {/* The details for screen readers, whether or not the tooltip is open */}
            <span id={`${notesId}-${i}`} style={visuallyHidden}>
              {note.details}
            </span>
          </React.Fragment>
        ))}
      </>
    );
  },
);

/**
 * VIS NODE
 */
export interface VisNodeProps {
  data: VisNodeData;
  id: string;
}

const VisNode: React.FC<VisNodeProps> = ({ data, id }) => {
  // The core plotting/graph view, as a separate component
  const visViewRef = useRef<VisViewRef | null>(null);

  const setDataPropsForNode = useStore((state) => state.setDataPropsForNode);
  const aiSupport = useStore((state) => state.globalSettings.aiSupport);

  const [status, setStatus] = useState<Status>(Status.NONE);
  const [pastInputs, setPastInputs] = useState<JSONCompatible>([]);
  const [responses, setResponses] = useState<LLMResponse[]>([]);
  // Where VisView puts its chart type button, in this node's header
  const [graphTypeSlot, setGraphTypeSlot] = useState<HTMLSpanElement | null>(
    null,
  );

  // On load of vis view
  // const setVisViewRef = useCallback((elem: VisViewRef) => {
  //   if (elem && !visViewRef.current) {
  //     visViewRef.current = elem;
  //     elem.resetControls(responses);
  //   }
  // }, [responses]);

  const handleOnConnect = useCallback(() => {
    // Grab the input node ids
    const input_node_ids = [data.input];

    grabResponses(input_node_ids)
      .then(function (resps) {
        if (resps && resps.length > 0) {
          // Store responses and extract + store vars
          // toReversed exists, but TypeScript does not see it.
          setResponses((resps as any).toReversed());

          visViewRef?.current?.resetControls(resps);
          // From here a React effect will detect the changes to these values and display a new plot
        }
      })
      .catch(console.error);
  }, [data]);

  if (data.input) {
    // If there's a change in inputs...
    if (data.input !== pastInputs) {
      setPastInputs(data.input);
      handleOnConnect();
    }
  }

  useEffect(() => {
    if (data.refresh && data.refresh === true) {
      // Recreate the visualization:
      setDataPropsForNode(id, { refresh: false });
      handleOnConnect();
    }
  }, [data, id, handleOnConnect, setDataPropsForNode]);

  return (
    <BaseNode classNames="vis-node" nodeId={id}>
      <NodeLabel
        title={data.title || "Vis Node"}
        nodeId={id}
        status={status}
        icon={"📊"}
        customButtons={[
          // The chart type button (see VisView), hidden while an AI plot shows
          <span
            key="graph-type"
            ref={setGraphTypeSlot}
            style={{ display: data.aiPlot?.code ? "none" : "inline-block" }}
          />,
          ...(data.aiPlot?.code
            ? [
                <AIPlotHeaderButtons
                  key="ai-plot-buttons"
                  plot={data.aiPlot}
                  onCodeChange={(code) =>
                    setDataPropsForNode(id, {
                      aiPlot: { ...data.aiPlot, code } as unknown as Dict,
                    })
                  }
                  onBack={() => setDataPropsForNode(id, { aiPlot: null })}
                />,
              ]
            : []),
          ...(aiSupport
            ? [
                <AIGenPlotPopover
                  key="ai-popover"
                  responses={responses}
                  onPlotReady={(aiPlot) =>
                    setDataPropsForNode(id, {
                      aiPlot: aiPlot as unknown as Dict,
                    })
                  }
                />,
              ]
            : []),
        ]}
      />
      {data.aiPlot?.code && (
        <AIPlotView plot={data.aiPlot} responses={responses} />
      )}
      {/* Kept mounted, so its controls keep their state while an AI plot shows */}
      <div style={{ display: data.aiPlot?.code ? "none" : undefined }}>
        <VisView
          ref={visViewRef}
          id={id}
          headerSlot={graphTypeSlot}
          responses={responses}
          data={data}
          whenReplotting={(isReplotting) =>
            setStatus(isReplotting ? Status.LOADING : Status.NONE)
          }
        />
      </div>
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="grouped-handle"
        style={{ top: "50%" }}
        onConnect={handleOnConnect}
      />
    </BaseNode>
  );
};

export default VisNode;
